import React, { useState, useEffect, useCallback } from "react";
import { toast } from "sonner";
import { api } from "../lib/api";
import { FileDown, Trash2, CalendarDays, RefreshCw } from "lucide-react";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription,
  AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger,
} from "./ui/alert-dialog";

const download = async (url, filename) => {
  const r = await api.get(url, { responseType: "blob" });
  const href = URL.createObjectURL(r.data);
  const a = document.createElement("a");
  a.href = href; a.download = filename; a.click();
  URL.revokeObjectURL(href);
};

function DayCard({ g }) {
  return (
    <div data-testid={`day-${g.date}`} className="bg-[#18181B] border border-white/10 rounded-2xl mb-4 overflow-hidden">
      <div className="px-4 py-3 flex items-center justify-between bg-white/5">
        <p className="font-display font-black text-lg flex items-center gap-2"><CalendarDays className="w-5 h-5 text-[#FF007F]" />{g.label}</p>
        <p className="text-sm text-[#A1A1AA] font-bold">{g.member_count} members · {g.visitor_count} visitors</p>
      </div>
      <div className="divide-y divide-white/5">
        {g.members.map((r, i) => (
          <div key={`m${i}`} className="px-4 py-2.5 flex items-center justify-between">
            <span className="font-bold">{r.member_name} <span className="text-[#71717A] font-mono text-xs ml-1">{r.number}</span></span>
            <span className="text-sm"><span className="text-[#00FA9A] font-bold">IN {r.in || "—"}</span> · <span className={r.out ? "text-[#FF007F] font-bold" : "text-[#FFB800] font-bold"}>{r.out ? `OUT ${r.out}` : "NOT SIGNED OUT"}</span></span>
          </div>
        ))}
        {g.visitors.map((v, i) => (
          <div key={`v${i}`} className="px-4 py-2.5 flex items-center justify-between">
            <span className="font-bold">{v.visitor_name} <span className="text-[#00F0FF] text-xs ml-1">VISITOR · seeing {v.visiting}</span></span>
            <span className="text-sm text-[#A1A1AA]">{v.time}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

export const WeeklyAttendance = ({ onChange }) => {
  const [days, setDays] = useState([]);
  const [reports, setReports] = useState([]);
  const load = useCallback(async () => {
    try {
      const [d, r] = await Promise.all([api.get("/attendance/daily"), api.get("/reports/weekly")]);
      setDays(d.data.days); setReports(r.data);
    } catch (e) { toast.error("Could not load sign-ins"); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const closeWeek = async () => {
    try {
      const { data } = await api.post("/reports/close-week");
      if (!data.cleared) { toast("No sign-ins to clear"); return; }
      await download(`/reports/weekly/${data.report_id}/pdf`, "yaz-signins-week.pdf");
      toast.success("Week saved as PDF and cleared");
      load(); onChange && onChange();
    } catch (e) { toast.error("Could not clear the week"); }
  };

  return (
    <div>
      <div className="flex flex-wrap items-center gap-2 mb-4">
        <p className="font-display font-bold text-sm tracking-widest text-[#A1A1AA] mr-auto">THIS WEEK, DAY BY DAY</p>
        <button data-testid="attendance-refresh" onClick={() => { load(); onChange && onChange(); }} className="h-11 px-4 rounded-xl bg-[#18181B] border border-white/10 text-[#00F0FF] font-bold flex items-center gap-2"><RefreshCw className="w-4 h-4" />Refresh</button>
        <button data-testid="download-week-pdf" onClick={() => download("/reports/current/pdf", "yaz-signins-this-week.pdf").catch(() => toast.error("Download failed"))} className="h-11 px-4 rounded-xl bg-[#00F0FF] text-black font-bold flex items-center gap-2"><FileDown className="w-4 h-4" />Download week PDF</button>
        <AlertDialog>
          <AlertDialogTrigger asChild>
            <button data-testid="clear-week-button" className="h-11 px-4 rounded-xl bg-[#FF3B30] text-white font-bold flex items-center gap-2"><Trash2 className="w-4 h-4" />Clear this week</button>
          </AlertDialogTrigger>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>Clear this week's sign-ins?</AlertDialogTitle>
              <AlertDialogDescription>A PDF of the week is saved and downloaded first, then the sign-in records are cleared. Memberships are not affected.</AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel data-testid="clear-week-cancel">Cancel</AlertDialogCancel>
              <AlertDialogAction data-testid="clear-week-confirm" onClick={closeWeek}>Save PDF &amp; clear</AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </div>
      {days.length === 0 && <div className="bg-[#18181B] border border-white/10 rounded-2xl px-4 py-8 text-center text-[#71717A] mb-4">No sign-ins this week yet.</div>}
      {days.map((g) => <DayCard key={g.date} g={g} />)}
      <p className="font-display font-bold text-sm tracking-widest text-[#A1A1AA] mt-6 mb-2">PAST WEEKS (PDF)</p>
      <div className="bg-[#18181B] border border-white/10 rounded-2xl divide-y divide-white/5">
        {reports.length === 0 && <div className="px-4 py-6 text-center text-[#71717A]">No saved weeks yet. Each week is saved automatically on Sunday at midnight.</div>}
        {reports.map((r) => (
          <div key={r.id} data-testid={`report-${r.id}`} className="px-4 py-3 flex items-center justify-between gap-3">
            <div>
              <p className="font-bold">{r.range}</p>
              <p className="text-xs text-[#71717A]">{r.member_signins} member sign-ins · {r.visitors} visitors · {r.source === "auto" ? "auto-saved" : "saved by staff"}</p>
            </div>
            <button data-testid={`report-download-${r.id}`} onClick={() => download(`/reports/weekly/${r.id}/pdf`, `yaz-signins-${r.created_at.slice(0, 10)}.pdf`)} className="h-10 px-3 rounded-xl bg-white/10 font-bold flex items-center gap-2"><FileDown className="w-4 h-4" />PDF</button>
          </div>
        ))}
      </div>
    </div>
  );
};
